import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { RoleKey, StaffContext, useStaffGate } from '../../lib/staffContext';
import type { NavItem } from '../../ui/StaffShell';
import type { Strings } from '../../i18n/strings';
import { rpc, SupportStatus } from './api';

export type SchoolRoleKey = Exclude<RoleKey, 'platform_admin'>;

/**
 * The platform-admin page gate: role check, support-mode status (for the
 * banner on every screen) and the number of approvals waiting (for the nav).
 */
export function usePlatformAdmin(): {
  ctx: StaffContext | null;
  ctxError: string | null;
  support: SupportStatus;
  approvals: number;
  refreshSupport: () => Promise<void>;
  signOut: () => Promise<void>;
} {
  const navigate = useNavigate();
  const { ctx, error: ctxError } = useStaffGate(['platform_admin']);
  const [support, setSupport] = useState<SupportStatus>({ active: false });
  const [approvals, setApprovals] = useState(0);

  const refreshSupport = useCallback(async () => {
    const [s, a] = await Promise.all([
      rpc<SupportStatus>('platform_support_status'),
      rpc<unknown[]>('platform_list_approvals'),
    ]);
    if (s.data) setSupport(s.data);
    if (a.data) setApprovals(a.data.length);
  }, []);

  useEffect(() => {
    if (ctx) refreshSupport();
  }, [ctx, refreshSupport]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }, [navigate]);

  return { ctx, ctxError, support, approvals, refreshSupport, signOut };
}

export type AdminSection = 'overview' | 'schools' | 'approvals' | 'staff' | 'log' | 'archive' | 'settings' | 'compliance' | 'insights' | 'content';

export function adminNav(t: Strings, active: AdminSection, approvals = 0): NavItem[] {
  return [
    { icon: '📊', label: t['admin.navOverview'], href: '/admin', active: active === 'overview' },
    { icon: '🏫', label: t['admin.navSchools'], href: '/admin/schools', active: active === 'schools' },
    {
      icon: '✅',
      label: approvals > 0 ? t['pa.navApprovalsN'](approvals) : t['pa.navApprovals'],
      href: '/admin/approvals',
      active: active === 'approvals',
    },
    { icon: '🧩', label: t['r7.cg.nav'], href: '/admin/content', active: active === 'content' },
    { icon: '📜', label: t['pa.navLog'], href: '/admin/log', active: active === 'log' },
    { icon: '🗄️', label: t['pa.navArchive'], href: '/admin/archive', active: active === 'archive' },
    { icon: '🔭', label: t['r3.ai.nav'], href: '/admin/insights', active: active === 'insights' },
    { icon: '📁', label: t['h2.navCompliance'], href: '/admin/compliance', active: active === 'compliance' },
    { icon: '⚙️', label: t['pa.navSettings'], href: '/admin/settings', active: active === 'settings' },
  ];
}
